import { CheckCircle2, Smartphone, LayoutDashboard } from "lucide-react";
import { STR } from "@/data/i18n";
import { useLang } from "@/lib/lang";
import { FadeUp, SectionHeading } from "./Shared";

const ROOM_PHOTOS = [
    "https://images.pexels.com/photos/28256945/pexels-photo-28256945.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.pexels.com/photos/28539228/pexels-photo-28539228.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.pexels.com/photos/237371/pexels-photo-237371.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];
const HERO_PHOTO =
    "https://images.unsplash.com/photo-1721222203415-69033eaf3bd1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHx0cm9waWNhbCUyMHZpbGxhJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzkyNTg5MjQ1fDA&ixlib=rb-4.1.0&q=85";
const DASHBOARD_PHOTO =
    "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYW5hbHl0aWNzJTIwZGFzaGJvYXJkfGVufDB8fHx8MTc5MjU4OTI0NXww&ixlib=rb-4.1.0&q=85";

const BrowserFrame = ({ url, children }) => (
    <div className="overflow-hidden rounded-xl border border-white/10 bg-[#0d0d0d] shadow-2xl shadow-black/50">
        <div className="flex items-center gap-2 border-b border-white/10 bg-[#161616] px-4 py-2.5">
            <span className="h-2.5 w-2.5 rounded-full bg-red-500/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-green-500/80" />
            <span className="ml-3 flex-1 truncate rounded-md bg-white/5 px-3 py-1 text-[10px] text-zinc-500">
                {url}
            </span>
        </div>
        {children}
    </div>
);

const CustomerMockup = ({ mock }) => (
    <div className="relative">
        <BrowserFrame url="www.propertianda.id">
            <div className="space-y-3 p-4">
                <div className="relative h-24 overflow-hidden rounded-lg">
                    <img
                        src={HERO_PHOTO}
                        alt="Contoh foto utama properti pada tampilan customer"
                        loading="lazy"
                        className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-2.5 left-3 space-y-1.5">
                        <div className="h-2 w-24 rounded bg-white/85" />
                        <div className="h-1.5 w-16 rounded bg-white/50" />
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                    {ROOM_PHOTOS.map((photo, n) => (
                        <div key={n} className="rounded-md border border-white/5 bg-white/5 p-1.5">
                            <img
                                src={photo}
                                alt={`Contoh foto kamar ${n + 1} pada galeri properti`}
                                loading="lazy"
                                className="h-10 w-full rounded object-cover"
                            />
                            <div className="mx-0.5 mt-1.5 h-1.5 w-4/5 rounded bg-white/25" />
                            <div className="mx-0.5 mt-1 h-1.5 w-2/5 rounded bg-amber-400/60" />
                        </div>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <div className="h-7 flex-1 rounded-md border border-white/10 bg-white/5 px-2 pt-2 text-[9px] text-zinc-500">
                        {mock.pickDate}
                    </div>
                    <div className="btn-flame h-7 rounded-md px-3 pt-1.5 text-[10px] font-semibold text-white">
                        {mock.bookNow}
                    </div>
                </div>
            </div>
        </BrowserFrame>
        <div className="absolute -bottom-6 -right-3 w-24 overflow-hidden rounded-2xl border-2 border-zinc-700 bg-[#0d0d0d] shadow-2xl shadow-black/60 sm:w-28 md:-right-6">
            <div className="mx-auto mt-1.5 h-1 w-8 rounded-full bg-zinc-700" />
            <div className="space-y-1.5 p-2">
                <img
                    src={ROOM_PHOTOS[0]}
                    alt="Contoh tampilan properti di HP"
                    loading="lazy"
                    className="h-14 w-full rounded-md object-cover"
                />
                <div className="h-1.5 w-4/5 rounded bg-white/30" />
                <div className="h-1.5 w-3/5 rounded bg-white/20" />
                <div className="btn-flame h-5 rounded pt-1 text-center text-[8px] font-semibold text-white">
                    {mock.payOnline}
                </div>
            </div>
        </div>
        <div className="absolute -left-3 top-6 flex items-center gap-1.5 rounded-full border border-white/10 bg-[#121212] px-3 py-1.5 shadow-lg md:-left-6">
            <Smartphone className="h-3.5 w-3.5 text-amber-400" />
            <span className="text-[10px] font-medium text-zinc-300">{mock.responsive}</span>
        </div>
    </div>
);

const DashboardMockup = ({ mock }) => (
    <div className="relative">
        <BrowserFrame url="dashboard.propertianda.id">
            <div className="flex">
                <div className="hidden w-20 space-y-2 border-r border-white/5 bg-[#101010] p-3 sm:block">
                    <div className="h-6 rounded bg-gradient-to-r from-red-600/60 to-amber-500/60" />
                    {[0, 1, 2, 3].map((n) => (
                        <div key={n} className="h-4 rounded bg-white/5" />
                    ))}
                </div>
                <div className="flex-1 space-y-3 p-4">
                    <div className="grid grid-cols-3 gap-2">
                        {mock.stats.map((label) => (
                            <div key={label} className="rounded-md border border-white/5 bg-white/5 p-2">
                                <div className="text-[8px] text-zinc-500">{label}</div>
                                <div className="mt-1 h-2.5 w-3/5 rounded bg-amber-400/70" />
                            </div>
                        ))}
                    </div>
                    <div className="relative h-16 overflow-hidden rounded-md border border-white/5">
                        <img
                            src={DASHBOARD_PHOTO}
                            alt="Contoh visual laporan analitik pada dashboard owner"
                            loading="lazy"
                            className="h-full w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/25" />
                        <span className="absolute left-2 top-1.5 rounded bg-black/50 px-1.5 py-0.5 text-[8px] font-medium text-amber-300">
                            {mock.report}
                        </span>
                    </div>
                    <div className="flex h-14 items-end gap-1.5 rounded-md border border-white/5 bg-white/5 p-3">
                        {[40, 65, 30, 80, 55, 90, 70, 45, 85, 60].map((h, i) => (
                            <div
                                key={i}
                                style={{ height: `${h}%` }}
                                className={`flex-1 rounded-sm ${
                                    i === 5
                                        ? "bg-gradient-to-t from-red-600 to-amber-400"
                                        : "bg-zinc-600/50"
                                }`}
                            />
                        ))}
                    </div>
                    <div className="space-y-1.5">
                        {[0, 1, 2].map((n) => (
                            <div key={n} className="flex items-center gap-2 rounded bg-white/5 px-2 py-1.5">
                                <div className="h-3 w-3 rounded-full bg-green-500/70" />
                                <div className="h-1.5 flex-1 rounded bg-white/20" />
                                <div className="h-1.5 w-8 rounded bg-amber-400/50" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </BrowserFrame>
        <div className="absolute -right-3 top-6 flex items-center gap-1.5 rounded-full border border-white/10 bg-[#121212] px-3 py-1.5 shadow-lg md:-right-6">
            <LayoutDashboard className="h-3.5 w-3.5 text-amber-400" />
            <span className="text-[10px] font-medium text-zinc-300">{mock.monitor}</span>
        </div>
    </div>
);

const SolutionCard = ({ testid, icon: Icon, title, desc, features, children }) => (
    <article data-testid={testid} className="card-surface relative overflow-hidden p-8 md:p-10">
        <div
            className="absolute -top-20 right-0 h-48 w-48 rounded-full bg-orange-600/10 blur-[80px]"
            aria-hidden="true"
        />
        <div className="relative">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-red-600/20 to-amber-500/20 text-amber-400">
                <Icon className="h-6 w-6" />
            </div>
            <h3 className="mt-5 text-2xl font-bold text-slate-50">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">{desc}</p>
            <ul className="mt-6 space-y-2.5">
                {features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-300">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                        {f}
                    </li>
                ))}
            </ul>
            <div className="mt-8">{children}</div>
        </div>
    </article>
);

export const Solutions = () => {
    const { lang } = useLang();
    const t = STR.solutions[lang];

    return (
        <section id="layanan" data-testid="solutions-section" className="relative py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-5 md:px-8">
                <SectionHeading eyebrow={t.eyebrow} title={t.title} subtitle={t.subtitle} />
                <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-10">
                    <FadeUp>
                        <SolutionCard
                            testid="solution-customer-card"
                            icon={Smartphone}
                            title={t.customer.title}
                            desc={t.customer.desc}
                            features={t.customer.features}
                        >
                            <CustomerMockup mock={t.mock} />
                        </SolutionCard>
                    </FadeUp>
                    <FadeUp delay={0.15}>
                        <SolutionCard
                            testid="solution-owner-card"
                            icon={LayoutDashboard}
                            title={t.owner.title}
                            desc={t.owner.desc}
                            features={t.owner.features}
                        >
                            <DashboardMockup mock={t.mock} />
                        </SolutionCard>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
};
